import moment from "moment";
import { Providers } from "@tripian/model";
import { displayableLivnInvoice } from "./livnPricing";
import { livnBookingCancellationPolicy, livnCancellationPolicyLines } from "./livnCancellation";

/**
 * Downloadable ticket/voucher required by Livn certification (feedback V2, general comment 1):
 * after a booking is confirmed the customer must be able to download a generated ticket.
 * The document mirrors what LivnBookingTickets renders on screen — and what Alice
 * (https://alice.livnapi.com/) generates for the same booking — as a PDF file.
 *
 * jspdf is loaded on demand so the library is only fetched when a ticket is downloaded.
 */

export type LivnTicketPdfInput = {
  bookings: Providers.Livn.Booking[];
  tourName?: string;
  /** Same markup used on screen, so the PDF invoice matches what the customer paid. */
  markupPerc?: number;
  /** Shown when the supplier omitted ticket.travelDate — same fallbacks the screen uses. */
  fallbackTravelDate?: string;
  fallbackTimeLabel?: string;
};

const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const MARGIN = 16;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const BARCODE_SIZE = 26;

const GRAY_DARK: [number, number, number] = [40, 40, 40];
const GRAY_MID: [number, number, number] = [100, 100, 100];
const GRAY_LIGHT: [number, number, number] = [150, 150, 150];
const AMBER: [number, number, number] = [180, 120, 0];

const barcodeImageFormat = (dataUri?: string): string | undefined => {
  const match = dataUri?.match(/^data:image\/(png|jpe?g|gif|webp)/i);
  if (!match) return undefined;
  return match[1].toUpperCase().startsWith("JP") ? "JPEG" : match[1].toUpperCase();
};

export const downloadLivnTicketsPdf = async ({
  bookings,
  tourName,
  markupPerc,
  fallbackTravelDate,
  fallbackTimeLabel,
}: LivnTicketPdfInput): Promise<void> => {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  let y = MARGIN;

  const ensureSpace = (height: number) => {
    if (y + height > PAGE_HEIGHT - MARGIN) {
      doc.addPage();
      y = MARGIN;
    }
  };

  type TextOpts = {
    size?: number;
    bold?: boolean;
    color?: [number, number, number];
    indent?: number;
    gapAfter?: number;
    /** Width kept free on the right, so text never runs under a barcode image. */
    reserveRight?: number;
  };
  const writeText = (text: string, opts: TextOpts = {}) => {
    const { size = 9, bold = false, color = GRAY_DARK, indent = 0, gapAfter = 1, reserveRight = 0 } = opts;
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.setTextColor(...color);
    const lineHeight = size * 0.42;
    const lines: string[] = doc.splitTextToSize(text, CONTENT_WIDTH - indent - reserveRight);
    lines.forEach(line => {
      ensureSpace(lineHeight);
      doc.text(line, MARGIN + indent, y);
      y += lineHeight;
    });
    y += gapAfter;
  };

  const divider = () => {
    ensureSpace(4);
    doc.setDrawColor(210, 210, 210);
    doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y);
    y += 4;
  };

  // ── Document header ──
  const reference = bookings[0]?.livnReference;
  writeText(tourName ?? bookings[0]?.productName ?? "Your Booking", { size: 16, bold: true, gapAfter: 1.5 });
  writeText("Booking Confirmation & Tickets", { size: 10, color: GRAY_MID, gapAfter: 1 });
  if (reference) writeText(`Reference: ${reference}`, { size: 10, color: GRAY_MID, gapAfter: 2 });
  divider();

  let ticketCounter = 0;

  bookings.forEach((booking, bookingIdx) => {
    if (bookingIdx > 0) {
      y += 2;
      divider();
    }

    const headerParts = [
      booking.livnReference ? `Booking ref: ${booking.livnReference}` : undefined,
      booking.supplierReference ? `Supplier ref: ${booking.supplierReference}` : undefined,
      booking.partyName ? `Lead traveller: ${booking.partyName}` : undefined,
      booking.partyEmailAddress,
      booking.confirmed ? `Confirmed ${moment(booking.confirmed).format("MMM D, YYYY HH:mm")}` : undefined,
    ].filter(Boolean);
    if (headerParts.length > 0) writeText(headerParts.join("   ·   "), { size: 8, color: GRAY_LIGHT });
    if (booking.productName && booking.productName !== tourName) {
      writeText(booking.productName, { size: 11, bold: true });
    }

    const allResources = booking.externalResources?.externalResources ?? [];
    const tickets = booking.tickets ?? [];

    if (tickets.length === 0) {
      writeText(
        "Your tickets are being issued by the supplier and will appear in your reservations as soon as they are available.",
        { size: 9, color: GRAY_MID }
      );
    }

    tickets.forEach(ticket => {
      ticketCounter += 1;
      const passengers = ticket.passengerDetails ?? [];
      const isGroupTicket = passengers.length > 1;

      ensureSpace(BARCODE_SIZE + 4);
      y += 2;

      // Barcode on the right of the ticket header, when the supplier issued one.
      const barcode = ticket.barcodeDetails?.barcodeData;
      const barcodeFormat = barcodeImageFormat(barcode);
      let barcodeDrawn = false;
      if (barcode && barcodeFormat) {
        try {
          doc.addImage(barcode, barcodeFormat, PAGE_WIDTH - MARGIN - BARCODE_SIZE, y - 3, BARCODE_SIZE, BARCODE_SIZE);
          barcodeDrawn = true;
        } catch {
          // A malformed image must not block the rest of the ticket; a notice is added below.
        }
      }

      // Every line of a barcoded ticket keeps clear of the barcode column, so a long
      // product name or note can never run underneath the image.
      const reserveRight = barcodeDrawn ? BARCODE_SIZE + 4 : 0;
      const ticketText = (text: string, opts: TextOpts = {}) => writeText(text, { ...opts, reserveRight });

      ticketText(isGroupTicket ? `Group Ticket (${passengers.length} travellers)` : `Ticket ${ticketCounter}`, {
        size: 11,
        bold: true,
      });

      // The supplier marked the barcode as needed but its image could not be embedded —
      // say so instead of failing silently, and point at the raw code printed below.
      if (barcode && !barcodeDrawn) {
        ticketText(
          ticket.barcodeDetails?.barcodeRaw
            ? "The barcode image could not be included — present the barcode number below at entry."
            : "The barcode image could not be included — please keep your booking reference at hand.",
          { size: 8, bold: true, color: AMBER, indent: 2, gapAfter: 0.5 }
        );
      }

      passengers.forEach(p => ticketText(p.name, { size: 10, indent: 2, gapAfter: 0.5 }));
      (ticket.productDetails ?? []).forEach(p =>
        ticketText(`${p.name}${p.startTime ? ` · ${p.startTime.slice(0, 5)}` : ""}`, {
          size: 8,
          color: GRAY_MID,
          indent: 2,
          gapAfter: 0.5,
        })
      );

      const travelDate = ticket.travelDate ?? fallbackTravelDate;
      const metaParts = [
        travelDate ? moment(travelDate).format("MMM D, YYYY") : undefined,
        !ticket.productDetails?.[0]?.startTime ? fallbackTimeLabel : undefined,
        ticket.supplierName,
      ].filter(Boolean);
      if (metaParts.length > 0) ticketText(metaParts.join("   ·   "), { size: 8, color: GRAY_LIGHT, indent: 2 });

      const pickup = ticket.pickupDetails;
      if (pickup?.pickupLocation) {
        ticketText(`Pickup: ${pickup.pickupLocation}${pickup.pickupTime ? ` at ${pickup.pickupTime.slice(0, 5)}` : ""}`, {
          size: 8,
          indent: 2,
          gapAfter: 0.5,
        });
      }
      if (pickup?.notes) ticketText(pickup.notes, { size: 8, color: GRAY_MID, indent: 2, gapAfter: 0.5 });
      if (pickup?.dropOffLocation) ticketText(`Drop-off: ${pickup.dropOffLocation}`, { size: 8, indent: 2, gapAfter: 0.5 });
      if (pickup?.dropoffNotes) ticketText(pickup.dropoffNotes, { size: 8, color: GRAY_MID, indent: 2, gapAfter: 0.5 });

      if (ticket.supplierEmailRes || ticket.supplierPhoneRes) {
        ticketText(
          `Supplier contact: ${[ticket.supplierEmailRes, ticket.supplierPhoneRes].filter(Boolean).join(" · ")}`,
          { size: 8, color: GRAY_MID, indent: 2, gapAfter: 0.5 }
        );
      }
      if (ticket.localFees) ticketText(`Payable locally: ${ticket.localFees}`, { size: 8, bold: true, color: AMBER, indent: 2, gapAfter: 0.5 });
      if (ticket.printRequired) {
        ticketText("This ticket must be printed and presented on the day.", { size: 8, bold: true, color: AMBER, indent: 2, gapAfter: 0.5 });
      }
      if (ticket.specialNotes) ticketText(ticket.specialNotes, { size: 8, color: GRAY_MID, indent: 2, gapAfter: 0.5 });
      if (ticket.barcodeDetails?.barcodeRaw) {
        ticketText(`Barcode: ${ticket.barcodeDetails.barcodeRaw}`, { size: 7, color: GRAY_LIGHT, indent: 2, gapAfter: 0.5 });
      }

      const ticketResources = allResources.filter(r => (ticket.uuid ? r.ticketUuids?.includes(ticket.uuid) : false));
      ticketResources.forEach(r =>
        ticketText(`${r.caption || "Ticket link"}${r.printRequired ? " (print required)" : ""}: ${r.url}`, {
          size: 8,
          color: GRAY_MID,
          indent: 2,
          gapAfter: 0.5,
        })
      );
      y += 1.5;
    });

    // Resources without ticketUuids cover the whole booking.
    allResources
      .filter(r => !r.ticketUuids?.length)
      .forEach(r =>
        writeText(`${r.caption || "Booking link"}${r.printRequired ? " (print required)" : ""}: ${r.url}`, {
          size: 8,
          color: GRAY_MID,
          gapAfter: 0.5,
        })
      );

    // ── Invoice — the exact derivation the screen uses, so the two can never disagree ──
    const invoiceView = displayableLivnInvoice(booking.invoice, markupPerc);
    if (invoiceView) {
      y += 1;
      divider();
      invoiceView.lines.forEach(line => {
        // The amount is right-aligned on the first baseline; the title wraps clear of it.
        const amount = `${line.displayed.amount} ${line.displayed.currency}`;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(...GRAY_MID);
        const amountWidth = doc.getTextWidth(amount) + 4;
        const firstLineY = (() => {
          ensureSpace(4);
          return y;
        })();
        doc.text(amount, PAGE_WIDTH - MARGIN, firstLineY, { align: "right" });
        writeText(`${line.title} × ${line.quantity}`, {
          size: 8,
          color: GRAY_MID,
          gapAfter: 0.5,
          reserveRight: amountWidth,
        });
      });
      ensureSpace(5);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...GRAY_DARK);
      doc.text("Total paid", MARGIN, y);
      doc.text(`${invoiceView.total} ${invoiceView.currency}`, PAGE_WIDTH - MARGIN, y, { align: "right" });
      y += 6;
    }

    const policyLines = livnCancellationPolicyLines(livnBookingCancellationPolicy(booking));
    if (policyLines.length > 0) {
      writeText("Cancellation Policy", { size: 9, bold: true, gapAfter: 0.5 });
      policyLines.forEach(line => writeText(line, { size: 8, color: GRAY_MID, gapAfter: 0.5 }));
    }
  });

  doc.save(`tickets-${reference ?? moment().format("YYYYMMDD-HHmm")}.pdf`);
};
